import classNames from "classnames"

import { Color } from "../../../constants/V2/color"
import GatsbyStoryblokImage from "../../elements/V2/GatsbyStoryblokImage"
import Typography from "../../elements/V2/Typography"
import Video from "../../experiments/Video"
import LayoutItem from "../Layouts/LayoutItem"

import TextButton from "@components/elements/V2/Buttons/TextButton"
import { getAccentColor, getBackgroundColorClass } from "@utils/V2/color"
import React from "react"

type Props = {
  title: string
  description?: string
  layoutStyle?: "single" | "card"
  backgroundImage: string
  backgroundImageAlt: string
  backgroundVideo?: string
  desktopCardColor?: Color
  mobileCardColor?: Color
  ctaText?: string
  ctaLink?: YocoCom.YocoLink
  ctaStyle?: "text" | "icon" | "underlined" | "underlined-reverse" | "none"
}

const VideoBanner = ({
  title,
  description,
  layoutStyle,
  backgroundImage,
  backgroundImageAlt,
  backgroundVideo,
  desktopCardColor,
  mobileCardColor,
  ctaText,
  ctaLink,
  ctaStyle,
}: Props) => {
  const hasCta = ctaLink && ctaText && ctaStyle
  const hasCardLayout = layoutStyle === "card"

  function MobileLayout({ backgroundColor }: { backgroundColor?: Color }) {
    return (
      <div
        className={classNames(
          "order-last col-span-full flex h-full flex-col justify-center rounded-12 text-left md:hidden",
          backgroundColor && getBackgroundColorClass(backgroundColor),
          hasCta && "justify-between"
        )}
      >
        <div>
          <Typography
            element="h2"
            text={title}
            size="lead-md"
            weight="medium"
            font="grotesk"
            color={getAccentColor(backgroundColor || Color.Black)}
          />

          {description && (
            <Typography
              text={description}
              size="body-md"
              weight="book"
              font="grotesk"
              color={getAccentColor(backgroundColor || Color.White)}
              className="mt-8"
            />
          )}
        </div>

        {hasCta && hasCardLayout && (
          <TextButton
            text={ctaText}
            color={getAccentColor(backgroundColor ?? Color.White)}
            style={ctaStyle}
            className="mt-16"
          />
        )}
      </div>
    )
  }

  function DesktopLayout({ backgroundColor }: { backgroundColor?: Color }) {
    return (
      <div
        className={classNames(
          "order-first col-span-2 hidden h-full flex-col rounded-12 bg-current p-[51px] text-left md:flex lg:col-span-4",
          backgroundColor && getBackgroundColorClass(backgroundColor),
          hasCta && "justify-between"
        )}
      >
        <div className="max-w-[230px]">
          <Typography
            element="h2"
            text={title}
            size="lead-md"
            weight="medium"
            font="grotesk"
            color={getAccentColor(backgroundColor || Color.Black)}
          />

          {description && (
            <Typography
              text={description}
              size="body-md"
              weight="book"
              font="grotesk"
              color={getAccentColor(backgroundColor || Color.White)}
              className="mt-20"
            />
          )}
        </div>

        {hasCta && hasCardLayout && (
          <TextButton
            text={ctaText}
            color={getAccentColor(backgroundColor ?? Color.White)}
            style={ctaStyle}
          />
        )}
      </div>
    )
  }

  return (
    <LayoutItem as="div" animateOnScroll className="col-span-full">
      <div className="grid grid-cols-4 items-center gap-grid-xs sm:grid-cols-4 sm:gap-grid-sm md:grid-cols-6 md:gap-grid-md lg:grid-cols-12 lg:gap-grid-lg xl:mx-auto xl:max-w-[1512px] xl:gap-grid-xl">
        {hasCardLayout ? (
          <>
            <MobileLayout backgroundColor={mobileCardColor} />

            <DesktopLayout backgroundColor={desktopCardColor} />
          </>
        ) : (
          <div className="col-span-full text-center md:col-span-2 md:text-left lg:col-span-4">
            <Typography
              text={title}
              font="grotesk"
              weight="medium"
              size="h3"
              color={Color.Black}
              element="h2"
            />
          </div>
        )}

        <div className="col-span-full md:col-span-4 lg:col-span-8">
          {backgroundVideo ? (
            <Video
              video={backgroundVideo}
              coverImage={backgroundImage}
              ImageAlt={backgroundImageAlt || "Video banner image"}
              aspectRatio="16:9"
              playbackMode="autoplay"
              className="rounded-lg"
            />
          ) : (
            <GatsbyStoryblokImage
              image={backgroundImage}
              alt={backgroundImageAlt || "Video banner image"}
              aspectRatio="16:9"
              className="rounded-lg"
            />
          )}
        </div>
      </div>
    </LayoutItem>
  )
}

export default VideoBanner
